'use client';

import React, { useEffect, useState } from 'react';
import FullScreenOverlay from '@/ui/components/shared/FullScreenOverlay';
import { urlParameterCodes } from '@/constants/urlParams';
import { useIsHydrated } from '@/ui/hooks/useIsHydrated';
import { useSearchParams } from 'next/navigation';
import axios from 'axios';
import { apiPaths } from '@/constants/apiPaths';
import UpgradeProgramType from '@/constants/upgrade-program-type';
import { useCartStore } from '@/ui/store/cart';
import SmartlockDetail from '../SmartlockDetail';
import { SmartLockContextProvider } from '../../../../providers/smartlockProvider';
import { BundleContextProvider } from '../../../../providers/bundleProvider';
import { Smartlock } from '../../../../services/SmartlockService';
import { NukiIdOverlay } from '../NukiIdOverlay';

const { productDetail } = urlParameterCodes;

export function SmartlockDetailOverlay({
  product,
  initialChooserOpen,
}: Readonly<{
  product: Smartlock;
  initialChooserOpen?: boolean;
}>) {
  const [nukiIds, setNukiIds] = useState<string[]>([]);
  const [nukiIdValidated, setNukiIdValidated] = useState(false);
  const searchParams = useSearchParams();
  const cartStore = useCartStore();
  const [upgradeProgramName, setUpgradeProgramName] = useState(
    searchParams.get(urlParameterCodes.productDetail.upgrade),
  );

  useEffect(() => {
    setUpgradeProgramName(searchParams.get(urlParameterCodes.productDetail.upgrade));
  }, [searchParams]);

  const isHydrated = useIsHydrated();

  if (!initialChooserOpen && !isHydrated) {
    return null;
  }

  const addUpgradeBundleToCart = (programName: string) => {
    const upgradeBundle = product.upgradeBundles?.find(
      (bundle) => bundle.documentExtRef === programName,
    );
    if (upgradeBundle) {
      const cartBundleItem = upgradeBundle.getBundleCartItems(
        upgradeBundle.defaultBundleProductVariants,
      );
      if (cartBundleItem) {
        nukiIds.forEach((nukiId) => {
          cartStore.addBundle({
            ...cartBundleItem,
            upgradeProgramDeviceId: nukiId,
          });
        });
      }
      cartStore.toggleCartDisplay();
    }
  };

  const onNukiIdNextStep = async () => {
    setNukiIdValidated(true);
    if (
      upgradeProgramName === UpgradeProgramType.ADAPTER_EXCHANGE ||
      upgradeProgramName === UpgradeProgramType.KP2NFC
    ) {
      addUpgradeBundleToCart(upgradeProgramName);
    }
  };

  const upgradeDeviceIdValidation = async (
    id: string,
    apiPath: string,
  ): Promise<{ valid: boolean; error?: string }> => {
    const { data } = await axios.post(apiPath, {
      programName: upgradeProgramName,
      devices: [{ deviceId: id }],
    });
    const firstDevice = data.data[0]; // We are only fetching result for one id
    const isEligible = firstDevice?.isEligible ?? false;
    const error = isEligible ? undefined : (firstDevice?.reason ?? 'Unknown Error has occurred');
    return { valid: isEligible, error };
  };

  if (product.allowUpgradeChooser && !!upgradeProgramName && !nukiIdValidated) {
    return (
      <NukiIdOverlay
        apiPath={apiPaths.devices.upgrade.isEligible}
        isValidId={upgradeDeviceIdValidation}
        product={product}
        onlyValidationFlow
        nukiIds={nukiIds}
        setNukiIds={setNukiIds}
        paramName="upgrade"
        allowMultiple={upgradeProgramName === 'adapterExchange'}
        onNextAction={onNukiIdNextStep}
        removeParamOnClose={false}
      />
    );
  }

  return (
    <FullScreenOverlay paramName={productDetail.chooserOpen} initialOpen={initialChooserOpen}>
      <BundleContextProvider
        availableBundles={product.bundles}
        availableSubscriptionBundles={product.subscriptionBundles}
        availableUpgradeBundles={product.upgradeBundles}
        hostProductId={product.id}
      >
        <SmartLockContextProvider>
          <SmartlockDetail
            smartlockProduct={product}
            upgradeProgramDeviceId={nukiIds.length > 0 ? nukiIds[0] : undefined}
          />
        </SmartLockContextProvider>
      </BundleContextProvider>
    </FullScreenOverlay>
  );
}
